import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import '@shared/styles/app.scss';
import Login from '@features/auth/pages/login/Login';
import Register from '@features/auth/pages/auth/Register';
import ForgotPassword from '@features/auth/pages/auth/ForgotPassword';
import { useDispatch, useSelector } from 'react-redux';
import { redirectTo, selectIsAuthLoading, selectRedirectTo } from '@shared/store/auth.duck';
import ResetPassword from '@features/auth/pages/auth/ResetPassword';
import CompleteRegistration from '@features/auth/pages/auth/CompleteRegistration';
import NotFoundPage from '@shared/pages/not-found-page/NotFoundPage';
import AccountDeletedPage from '@shared/pages/account-deleted-page/AccountDeletedPage';
import SecureApp from '@shared/routing/SecureApp';
import Notification from '@shared/ui/molecules/notification/Notification';
import { getParameterByName } from '@shared/utils/UrlUtils';
import { successNotification } from '@shared/store/notification.duck';
import { getNotificationMessage, NotificationMessages } from '@shared/models/notification.models';
import VerificationRequired from '@features/auth/pages/auth/verification-required/VerificationRequired';
import { usePageName } from '@shared/hooks/UsePageName';
import UnsubscribePage from '@shared/pages/UnsubscribePage';
import ScrollToTop from '@shared/routing/ScrollToTop';
import RegisterWithAzure from "@features/auth/pages/auth/RegisterWithAzure";
import LoginWithAzure from "@features/auth/pages/login/LoginWithAzure";
import LogoutWithAzure from "@features/auth/pages/auth/logout/LogoutWithAzure";
import { DynamicDialogRenderer } from '@shared/ui/organisms/DynamicDialogRenderer';
import RegisterWithTeamToday from "@features/auth/pages/auth/RegisterWithTeamToday";
import LoginWithGoogle from "@features/auth/pages/login/LoginWithGoogle";
import InviteLogin from "@features/auth/pages/login/InviteLogin";
import McpConsentPage from "@shared/pages/mcp-consent/McpConsentPage";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { WithMsTeamsAppChecker } from '@shared/hooks/WithMsTeamsAppChecker';
import { OfficeCheckinPage } from "@features/whereabouts/pages/office-checkin/OfficeCheckinPage";
import { KioskPage } from "@features/kiosk/pages/KioskPage";
import { SignagePage } from "@features/signage/pages/SignagePage";
import { WithAuth } from '@shared/hooks/WithAuth';
import { resetAllLoginStorages } from '@shared/utils/AuthUtils';
import { WithTenantChecker } from '@shared/hooks/WithTenantChecker';
import '@shared/translations/config';
import LoginWithAzureInTeamsTab, { isOnTeamsTabAuthPage } from "@features/auth/pages/login/LoginWithAzureInTeamsTab";
import LoginWithAdminConsent from "@features/auth/pages/login/LoginWithAdminConsent";
import LoginWithAdminConsentEnd from "@features/auth/pages/login/LoginWithAdminConsentEnd";
import i18next from "i18next";
import moment from "moment";
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/es';
import { WithDateLocale } from '@shared/hooks/WithDateLocale';
import { WithMsTeamsNewUserChecker } from '@shared/hooks/WithMsTeamsNewUserChecker';
import LoginWithAzureInTeamsTabEnd from "@features/auth/pages/login/LoginWithAzureInTeamsTabEnd";
import MsalDebugPage from "@shared/pages/MsalDebugPage";
import { trackPageView } from '@shared/services/MarketingAnalytics';

// Reset moment locale after importing locale files (imports activate the last imported locale as a side-effect)
moment.locale(window.localStorage.getItem('tt-lang') || 'en');

export const PUBLIC_ROUTES = ["/",
  "/404",
  "/account-deleted",
  "/unsubscribe",
  "/login",
  "/invite",
  "/login/azure",
  "/login/tab-auth",
  "/login/admin-consent",
  "/login/admin-consent-end",
  "/login/google",
  "/logout/azure",
  "/verification-required",
  "/register",
  "/register/team-today",
  "/register/azure",
  "/forgot-password",
  "/reset-password",
  "/complete-registration",
  "/debug",
  "/debug/msal",
  "/confirm",
  "/kiosk",
  "/signage"];

export function isOnPublicPage(): boolean {
  return PUBLIC_ROUTES.includes(window.location.pathname);
}

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const redirectToUrl = useSelector(selectRedirectTo);
  const isAuthLoading = useSelector(selectIsAuthLoading);
  document.body.style.backgroundColor = 'white';

  usePageName(location.pathname);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  WithDateLocale();
  WithAuth();
  WithTenantChecker();
  WithMsTeamsAppChecker();
  WithMsTeamsNewUserChecker();

  useEffect(() => {
    const lang = window.localStorage.getItem('tt-lang') || 'en';
    if (lang) {
      moment.locale(lang);
      i18next.changeLanguage(lang, (err, t) => {
        if (err) return console.log('something went wrong loading', err);
      });
    }
  }, []);

  // Handle redirect notification
  useEffect(() => {
    const notificationParam = getParameterByName('not', window.location.href);
    const notificationTimeParam = getParameterByName('t', window.location.href) || '0';
    if (notificationParam === NotificationMessages.noAccountFound || notificationParam?.includes(NotificationMessages.azureLoginFailed)) {
      resetAllLoginStorages();
    }
    if (notificationParam && Date.now() - parseInt(notificationTimeParam) < 500) {
      dispatch(successNotification(getNotificationMessage(notificationParam as NotificationMessages)));
    }
  }, [dispatch]);

  if ((isAuthLoading && !isOnTeamsTabAuthPage())) {
    return <LoadingSpinner fullScreen={true} message={"general.app-loading"}/>
  }

  if (redirectToUrl) {
    // Compare only the path portion so redirect targets that carry a query string / hash
    // (e.g. a requested deep link restored after login) still clear once we've arrived.
    const redirectPath = redirectToUrl.split(/[?#]/)[0];
    if (location.pathname !== redirectPath) {
      return (<Redirect to={redirectToUrl}/>);
    } else {
      dispatch(redirectTo(undefined));
    }
  }

  return (
    <React.Fragment>
      <ScrollToTop>
        <Switch>
          <Route exact path="/"><Redirect to="/login"/></Route>
          <Route exact path="/404" component={NotFoundPage}/>
          <Route exact path="/account-deleted" component={AccountDeletedPage}/>
          <Route exact path="/unsubscribe" component={UnsubscribePage}/>

          {/* Login and register */}
          <Route exact path="/login" component={Login}/>
          <Route exact path="/invite" component={InviteLogin}/>
          <Route exact path="/login/azure" component={LoginWithAzure}/>
          <Route exact path="/login/tab-auth" component={LoginWithAzureInTeamsTab}/>
          <Route exact path="/login/tab-auth-end" component={LoginWithAzureInTeamsTabEnd}/>
          <Route exact path="/login/admin-consent" component={LoginWithAdminConsent}/>
          <Route exact path="/login/admin-consent-end" component={LoginWithAdminConsentEnd}/>
          <Route exact path="/login/google" component={LoginWithGoogle}/>

          <Route exact path="/logout/azure" component={LogoutWithAzure}/>

          <Route exact path="/verification-required" component={VerificationRequired}/>

          <Route exact path="/register" component={Register}/>
          <Route exact path="/register/team-today" component={RegisterWithTeamToday}/>
          <Route exact path="/register/azure" component={RegisterWithAzure}/>

          <Route exact path="/forgot-password" component={ForgotPassword}/>
          <Route exact path="/reset-password" component={ResetPassword}/>
          <Route exact path="/complete-registration" component={CompleteRegistration}/>


          <Route exact path="/confirm" component={OfficeCheckinPage}/>
          <Route exact path="/kiosk" component={KioskPage}/>
          <Route exact path="/signage" component={SignagePage}/>
          <Route exact path="/mcp-consent" component={McpConsentPage}/>
          <Route exact path="/debug/msal" component={MsalDebugPage}/>

          <SecureApp/>

        </Switch>
        <Notification/>
        <DynamicDialogRenderer/>
      </ScrollToTop>
    </React.Fragment>
  );
}

export default App;
